import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import { omit } from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import useElapsedTime from 'services/hooks/useElapsedTime';
import submissionService from 'services/submission';
import {
    getAIPreview,
    getCriterionMaxPoints,
    getRubricMaxPoints,
    getTemplatedFeedback,
    isValidRate,
    stripHTML,
} from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, currentAssignment, submissions: allSubmissions, setSubmissions, studentAccountUsers } = useApp();
    const { getAccount } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { joyrideRun, setJoyridePaused, setNextCallback, setPrevCallback, performNext } = useJoyride();
    const { id: currentAssignmentId, submissionId: currentSubmissionId } = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { onMessage } = useWebsocket();

    const [selectedFileId, setSelectedFileId] = useState('');
    const [sort, setSort] = useState('default');
    const [form, setForm] = useState({
        criteria: {},
        feedback: null,
        inline_comments: null,
        grading_explanations: {},
    });
    const [gradeOverrides, setGradeOverrides] = useState({});
    const [meta, setMeta] = useState({
        hasSeenFeedback: true,
        sessionStart: new Date().getTime(),
        excludedTime: 0,
    });
    const [tab, setTab] = useState('grades');
    const [selectedCriterionId, setSelectedCriterionId] = useState(null);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [selectedInlineCommentId, setSelectedInlineCommentId] = useState(null);
    const [isGradesGenerating, setIsGradesGenerating] = useState(false);
    const [isFeedbackGenerating, setIsFeedbackGenerating] = useState(false);
    const [showFailedDialog, setShowFailedDialog] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);

    const { createSession, addSessionToSubmission } = useElapsedTime({ meta, setMeta });

    const submissions = allSubmissions?.filter(
        ({ ai, grading, is_failed, locked }) => ((Boolean(ai) || is_failed) && !Boolean(grading)) || locked
    );
    const headerNavigationOptions = useMemo(
        () =>
            submissions
                ?.map(({ id, ai, files, grading, grade_overrides, api }) => {
                    return {
                        key: id,
                        value: api?.user?.name ?? files[0]?.original_file?.name ?? files[0]?.name,
                        number: submissions.findIndex((submission) => submission.id === id) + 1,
                        isGraded: Boolean(grading),
                        suggestedGrade: currentAssignment.rubric.criteria.reduce((acc, criterion) => {
                            const optionId = ai?.grading?.[criterion.id]?.optionId;
                            const option = criterion.options.find(({ id }) => id === optionId);
                            if (!option) return null;
                            return acc + Number(option.rate);
                        }, 0),
                        actualGrade: !grading
                            ? null
                            : grade_overrides?.total
                              ? Number(grade_overrides?.total)
                              : currentAssignment.rubric.criteria.reduce((acc, { id, options }) => {
                                    return grade_overrides?.[id]
                                        ? acc + Number(grade_overrides?.[id])
                                        : acc +
                                              Number(options.find((option) => option.id === grading.criteria[id]).rate);
                                }, 0),
                        maxPossibleGrade: currentAssignment.rubric.criteria.reduce((acc, { options }) => {
                            const max = Math.max(...options.map(({ rate }) => Number(rate)));
                            return acc + max;
                        }, 0),
                        section: api?.user?.sections
                            ? api?.user?.sections?.map(({ name }) => name)?.join(', ')
                            : api?.user?.section?.name,
                    };
                })
                ?.sort((a, b) => {
                    const gradeA = a.actualGrade ?? null;
                    const gradeB = b.actualGrade ?? null;
                    const suggestedA = a.suggestedGrade ?? null;
                    const suggestedB = b.suggestedGrade ?? null;

                    if (sort === 'title-asc') {
                        return a.value.localeCompare(b.value);
                    }
                    if (sort === 'title-desc') {
                        return b.value.localeCompare(a.value);
                    }
                    if (sort === 'grade-asc') {
                        if (gradeA !== null && gradeB !== null) {
                            return gradeA - gradeB;
                        }
                        if (gradeA !== null && gradeB === null) return -1;
                        if (gradeA === null && gradeB !== null) return 1;
                        if (gradeA === null && gradeB === null && suggestedA !== null && suggestedB !== null) {
                            return suggestedA - suggestedB;
                        }
                        if (suggestedA !== null && suggestedB === null) return -1;
                        if (suggestedA === null && suggestedB !== null) return 1;
                        return 0;
                    }
                    if (sort === 'grade-desc') {
                        if (gradeA !== null && gradeB !== null) {
                            return gradeB - gradeA;
                        }
                        if (gradeA !== null && gradeB === null) return -1;
                        if (gradeA === null && gradeB !== null) return 1;
                        if (gradeA === null && gradeB === null && suggestedA !== null && suggestedB !== null) {
                            return suggestedB - suggestedA;
                        }
                        if (suggestedA !== null && suggestedB === null) return -1;
                        if (suggestedA === null && suggestedB !== null) return 1;
                        return 0;
                    }
                    return 0;
                }),
        [currentAssignment?.rubric?.criteria, sort, submissions]
    );
    const currentSubmission = submissions?.find(({ id }) => id === currentSubmissionId);

    const selectedCriterionTitle = currentAssignment?.rubric?.criteria?.find(
        ({ id }) => id === selectedCriterionId
    )?.title;

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? [])
                .filter((item) => {
                    if (typeof item === 'string') return true;
                    if (item.file_id === selectedFileId) return true;
                    return false;
                })
                .map((item) => {
                    return {
                        id: selectedInlineCommentId ?? selectedCriterionId ?? selectedFeedbackId,
                        value: typeof item === 'string' ? item : item.highlight,
                    };
                });
        };

        if (tab === 'grades' && selectedCriterionId) {
            const locations = currentSubmission?.ai?.grading?.[selectedCriterionId]?.locations;
            return format(locations);
        }

        if (tab === 'feedback' && selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId);
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        const comments = form.inline_comments ?? [];
        const activeHighlights = comments
            .filter(({ active }) => active)
            .filter(({ id }) => id === selectedInlineCommentId)
            .map(({ highlight }) => highlight);

        return format(activeHighlights);
    }, [
        currentSubmission?.ai?.feedback,
        currentSubmission?.ai?.grading,
        form.feedback?.improvement_suggestions,
        form.inline_comments,
        selectedCriterionId,
        selectedFeedbackId,
        selectedFileId,
        selectedInlineCommentId,
        tab,
    ]);

    const isFormValid = useMemo(() => {
        if (!currentAssignment) return false;
        const isGradingValid = currentAssignment.rubric.criteria.every(
            ({ id }) => Boolean(form.criteria[id]) || Boolean(gradeOverrides[id])
        );
        const isFeedbackValid = Boolean(form.feedback);

        if (currentAssignment?.ai_feedback_enabled) {
            return isGradingValid && isFeedbackValid;
        } else if (currentAssignment?.ai_grading_enabled) {
            return isGradingValid;
        }
    }, [currentAssignment, form.criteria, form.feedback, gradeOverrides]);

    const areOverridesValid = useMemo(() => {
        if (!Object.entries(gradeOverrides).length) return true;
        return Object.entries(gradeOverrides)
            .map(([criterionId, overrideValue]) => {
                if (criterionId === 'total') {
                    return (
                        isValidRate(overrideValue) &&
                        Number(overrideValue) >= 0 &&
                        Number(overrideValue) <= getRubricMaxPoints(currentAssignment.rubric.criteria)
                    );
                } else {
                    return (
                        isValidRate(overrideValue) &&
                        Number(overrideValue) >= 0 &&
                        Number(overrideValue) <=
                            getCriterionMaxPoints(
                                currentAssignment.rubric.criteria.find(({ id }) => id === criterionId)
                            )
                    );
                }
            })
            .reduce((acc, cur) => acc && cur, true);
    }, [currentAssignment?.rubric?.criteria, gradeOverrides]);

    const isCurrentFormValid = useMemo(() => isFormValid && areOverridesValid, [isFormValid, areOverridesValid]);

    const goToNextSubmission = () => {
        let nextSubmissionIndex = headerNavigationOptions.findIndex(({ key }) => key === currentSubmission.id) + 1;
        if (nextSubmissionIndex === headerNavigationOptions.length) nextSubmissionIndex = 0;
        const nextSubmission = headerNavigationOptions[nextSubmissionIndex];
        const nextSubmissionId = nextSubmission.key;
        history.push(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`);
        setForm({
            criteria: nextSubmission?.grading?.criteria ?? {},
            feedback: nextSubmission?.ai?.feedback ?? null,
            inline_comments: nextSubmission?.ai?.inline_comments ?? null,
            grading_explanations: nextSubmission?.grading_explanations ?? {},
        });
        setMeta({
            hasSeenFeedback: nextSubmission.has_seen_feedback ?? false,
            sessionStart: new Date().getTime(),
            excludedTime: 0,
        });
    };

    const handleClickAcceptSuggestions = () => {
        setForm((current) => {
            return {
                ...current,
                criteria: {
                    ...current.criteria,
                    ...Object.entries(omit(currentSubmission.ai.grading, Object.keys(current.criteria))).reduce(
                        (acc, [criterionId, { optionId }]) => {
                            return {
                                ...acc,
                                [criterionId]: optionId,
                            };
                        },
                        {}
                    ),
                },
            };
        });
        setGradeOverrides({});
    };

    const handleClickConfirm = (shouldGoNext) => {
        if (joyrideRun) return performNext();
        document.getElementById('actions-container').scroll({ top: 0, behavior: 'smooth' });
        const updatedForm =
            currentSubmission.ai?.feedback?.feedback_type === 'templated'
                ? {
                      ...form,
                      feedback: {
                          ...form.feedback,
                          commentary: getTemplatedFeedback(currentAssignment, form),
                      },
                  }
                : form;
        const session = createSession('confirm');
        let updatedSubmission = addSessionToSubmission(session, currentSubmission);
        setSubmissions((current) => {
            return current.map((submission) => {
                if (submission.id !== currentSubmissionId) return submission;
                updatedSubmission = {
                    ...updatedSubmission,
                    modified_on: new Date().getTime(),
                    locked: true,
                    grading: omit(updatedForm, ['grading_explanations']),
                    has_seen_feedback: true,
                    grade_overrides: Object.fromEntries(
                        Object.entries(gradeOverrides).map(([key, value]) => [key, String(Number(value))])
                    ),
                    ...(Object.keys(form.grading_explanations).length && {
                        grading_explanations: form.grading_explanations,
                    }),
                };
                submissionService.updateSubmission(
                    currentSubmissionId,
                    omit(updatedSubmission, ['_id', 'locked', 'is_failed'])
                );
                return updatedSubmission;
            });
        });
        if (shouldGoNext) {
            goToNextSubmission();
        } else {
            setSubmissions((current) => {
                return current.map((submission) => omit(submission, ['locked']));
            });
            history.push(`/assignments/${currentAssignmentId}`);
        }
    };

    const handleClickNext = () => {
        if (Boolean(currentSubmission.grading)) {
            goToNextSubmission();
        } else {
            setDialog({
                open: true,
                message: t('dialog.unconfirmed-grades.title', 'Unconfirmed Grades and Feedback'),
                description: t(
                    'dialog.unconfirmed-grades.description',
                    'If you continue to the next submission, your unconfirmed grades/feedback will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const session = createSession('skip');
                    const updatedSubmission = addSessionToSubmission(session, currentSubmission);
                    setSubmissions((current) => {
                        return current.map((submission) => {
                            if (submission.id !== currentSubmissionId) return submission;
                            return updatedSubmission;
                        });
                    });
                    submissionService.updateSubmission(currentSubmissionId, omit(updatedSubmission, ['_id']));
                    goToNextSubmission();
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleSelectSubmission = (submissionId) => {
        if (Boolean(currentSubmission.grading)) {
            history.push(`/assignments/${currentAssignmentId}/grading/${submissionId}`);
        } else {
            setDialog({
                open: true,
                message: t('dialog.unconfirmed-grades.title', 'Unconfirmed Grades and Feedback'),
                description: t(
                    'dialog.unconfirmed-grades.description',
                    'If you continue to the next submission, your unconfirmed grades/feedback will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    const session = createSession('skip');
                    const updatedSubmission = addSessionToSubmission(session, currentSubmission);
                    setSubmissions((current) => {
                        return current.map((submission) => {
                            if (submission.id !== currentSubmissionId) return submission;
                            return updatedSubmission;
                        });
                    });
                    submissionService.updateSubmission(currentSubmissionId, omit(updatedSubmission, ['_id']));
                    history.push(`/assignments/${currentAssignmentId}/grading/${submissionId}`);
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickClose = () => {
        if (!currentSubmission.locked) {
            const session = createSession('exit');
            const updatedSubmission = addSessionToSubmission(session, currentSubmission);
            setSubmissions((current) => {
                return current
                    .map((submission) => {
                        if (submission.id !== currentSubmissionId) return submission;
                        return updatedSubmission;
                    })
                    .map((submission) => omit(submission, ['locked']));
            });
            submissionService.updateSubmission(currentSubmissionId, omit(updatedSubmission, ['_id', 'locked']));
        } else {
            setSubmissions((current) => {
                return current.map((submission) => omit(submission, ['locked']));
            });
        }
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const handleClickEdit = () => {
        setSubmissions((current) => {
            return current.map((submission) => {
                if (submission.id !== currentSubmission.id) return submission;
                setTimeout(() => {
                    setForm({
                        ...currentSubmission.grading,
                        grading_explanations: currentSubmission.grading_explanations ?? {},
                    });
                    setGradeOverrides(currentSubmission.grade_overrides ?? {});
                    setMeta((current) => ({ ...current, hasSeenFeedback: true }));
                }, 0);
                return omit(currentSubmission, ['grading', 'locked']);
            });
        });
    };

    const handleClickRegenerateFeedback = () => {
        setDialog({
            open: true,
            message: t('dialog.regenerate-suggested-feedback.title', 'Regenerate Suggested Feedback?'),
            description: t(
                'dialog.regenerate-suggested-feedback.description',
                'This will delete any existing feedback permanently.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.regenerate', 'Regenerate'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setIsFeedbackGenerating(true);
                const account = await getAccount();
                await getAIPreview({
                    target: 'feedback',
                    course_id: course.id,
                    owner_account_id: currentAssignment.owner_account_id,
                    assignment: currentAssignment,
                    submission: {
                        ...currentSubmission,
                        grading: omit(form, ['grading_explanations']),
                        grade_overrides: Object.fromEntries(
                            Object.entries(gradeOverrides).map(([key, value]) => [key, String(Number(value))])
                        ),
                        ...(Object.keys(form.grading_explanations).length && {
                            grading_explanations: form.grading_explanations,
                        }),
                    },
                    connections: account.connections.map(({ id }) => id),
                    language: course.language ?? 'English',
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleSelectDropdown = async ({ key }) => {
        if (key === 'regenerate-grades') {
            setDialog({
                open: true,
                message: t('dialog.regenerate-suggested-grades.title', 'Regenerate Suggested Grades?'),
                description: t(
                    'dialog.regenerate-suggested-grades.description',
                    'This will delete any existing grades permanently.'
                ),
                note: '',
                variant: 'primary',
                actionButtonText: t('common:controls.regenerate', 'Regenerate'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: async () => {
                    closeDialog();
                    setIsGradesGenerating(true);
                    const account = await getAccount();
                    await getAIPreview({
                        target: 'grading',
                        course_id: course.id,
                        owner_account_id: currentAssignment.owner_account_id,
                        assignment: currentAssignment,
                        submission: currentSubmission,
                        connections: account.connections.map(({ id }) => id),
                        language: course.language ?? 'English',
                    });
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
        if (key === 'regenerate-feedback') {
            handleClickRegenerateFeedback();
        }
        if (key === 'copy-all') {
            let feedback = '';
            if (typeof currentSubmission.ai?.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else {
                feedback += stripHTML(form.feedback.commentary);
                if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                    form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                        if (index || form.feedback.commentary) {
                            feedback += '\n\n';
                        }
                        const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                        feedback += `${criterion.title}\n\n`;
                        feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                        feedback +=
                            !positive.active && positive.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : positive.text === ''
                                  ? t(
                                        'feedback-section.no-feedback-no-pushback',
                                        'No feedback provided. This message will not be passed back.'
                                    )
                                  : stripHTML(positive.text);
                        feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                        feedback +=
                            !negative.active && negative.text
                                ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                                : negative.text === ''
                                  ? t(
                                        'feedback-section.no-feedback-no-pushback',
                                        'No feedback provided. This message will not be passed back.'
                                    )
                                  : stripHTML(negative.text);
                    });
                } else if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                    form.feedback.improvement_suggestions
                        .filter(({ active }) => active)
                        .forEach(({ text }, index) => {
                            if (index || form.feedback.commentary) {
                                feedback += '\n\n';
                            }
                            feedback += stripHTML(text);
                        });
                }
            }
            await navigator.clipboard.writeText(feedback);
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
                description: t(
                    'dialog.delete-all-feedback.description',
                    'Are you sure you want to delete all feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        positive: {
                                            ...item.positive,
                                            active: false,
                                        },
                                        negative: {
                                            ...item.negative,
                                            active: false,
                                        },
                                    })),
                                },
                            };
                        });
                    }
                    if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                        setForm((current) => {
                            return {
                                ...current,
                                feedback: {
                                    commentary: '',
                                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => ({
                                        ...item,
                                        active: false,
                                    })),
                                },
                            };
                        });
                    }
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleCriterionNavigation = (criterionId) => {
        setSelectedCriterionId(criterionId);
        setTab(tab === 'grades' ? 'feedback' : 'grades');
        const matchedSuggestion = form?.feedback?.improvement_suggestions?.find((s) => s.criterion_id === criterionId);
        if (matchedSuggestion) {
            setSelectedFeedbackId(matchedSuggestion.id);
        }
    };

    useEffect(() => {
        if (tab === 'feedback') {
            setMeta((current) => ({ ...current, hasSeenFeedback: true }));
        }
    }, [tab]);

    useEffect(() => {
        if (submissions && !submissions.length) {
            history.push(`/assignments/${currentAssignmentId}`);
        }
    }, [currentAssignmentId, history, submissions]);

    useEffect(() => {
        if (submissions?.length && !currentSubmissionId) {
            const nextSubmissionId = submissions[0].id;
            history.replace(`/assignments/${currentAssignmentId}/grading/${nextSubmissionId}`);
        }
    }, [currentAssignmentId, currentSubmissionId, history, submissions]);

    useEffect(() => {
        if (!currentSubmission?.id) return;
        setTab('grades');
    }, [currentSubmission?.id]);

    useEffect(() => {
        if (!currentAssignment) return;
        if (!currentSubmission) {
            setSelectedFileId('');
            setSelectedCriterionId(null);
            setSelectedFeedbackId(null);
            setSelectedInlineCommentId(null);
            return;
        }

        setSelectedFileId(currentSubmission.files[0].id);
        setSelectedCriterionId(currentAssignment.rubric.criteria[0].id);
        document.getElementById('actions-container').scrollTop = 0;
        if (currentSubmission.grading) {
            setForm({
                criteria: currentSubmission.grading.criteria,
                feedback: currentSubmission.grading.feedback,
                inline_comments: currentSubmission.grading.inline_comments,
                grading_explanations: currentSubmission.grading_explanations ?? {},
            });
            setGradeOverrides(currentSubmission.grade_overrides ?? {});
            setMeta({
                hasSeenFeedback: currentSubmission.has_seen_feedback ?? false,
                sessionStart: new Date().getTime(),
                excludedTime: 0,
            });
            if (['rubric', 'system'].includes(currentSubmission?.ai?.feedback?.feedback_type)) {
                const activeSuggestions = currentSubmission.grading.feedback.improvement_suggestions.filter(
                    ({ active }) => active
                );
                if (activeSuggestions.length) setSelectedFeedbackId(activeSuggestions[0].id);
            }
        } else if (currentSubmission.ai) {
            setForm({
                criteria: {},
                feedback: currentSubmission.ai.feedback,
                inline_comments: currentSubmission.ai.inline_comments,
                grading_explanations: currentSubmission.grading_explanations ?? {},
            });
            setGradeOverrides({});
            setMeta({
                hasSeenFeedback: currentSubmission.has_seen_feedback ?? false,
                sessionStart: new Date().getTime(),
                excludedTime: 0,
            });
            if (['rubric', 'system'].includes(currentSubmission.ai.feedback?.feedback_type)) {
                if (currentSubmission.ai.feedback.improvement_suggestions?.length) {
                    setSelectedFeedbackId(currentSubmission.ai.feedback.improvement_suggestions[0].id);
                }
            }
        } else {
            setForm({
                criteria: {},
                feedback: null,
                inline_comments: null,
                grading_explanations: {},
            });
            setGradeOverrides({});
            setMeta({
                hasSeenFeedback: true,
                sessionStart: new Date().getTime(),
                excludedTime: 0,
            });
        }
    }, [currentAssignment, currentSubmission]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    useEffect(() => {
        setNextCallback({
            8: () => setTab('feedback'),
            9: () => {
                setForm((current) => ({
                    ...current,
                    criteria: {
                        c1: 'c1-o1',
                        c2: 'c2-o2',
                        c3: 'c3-o2',
                        c4: 'c4-o3',
                    },
                }));
            },
        });
        setPrevCallback({
            8: () => setTab('grades'),
            10: () => {
                setForm((current) => ({ ...current, criteria: {} }));
            },
        });
    }, [setNextCallback, setPrevCallback]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.data?.payload_type === 'regenerate_submission') {
                const { submission } = message.data.payload;
                const updatedSubmission = {
                    ...currentSubmission,
                    modified_on: new Date().getTime(),
                };
                if (isGradesGenerating) {
                    updatedSubmission.ai.grading = submission.data.grading;
                }
                if (isFeedbackGenerating) {
                    updatedSubmission.ai.feedback = submission.data.feedback;
                    updatedSubmission.has_seen_feedback = tab === 'feedback';
                    if (updatedSubmission.grading) {
                        updatedSubmission.grading.feedback = submission.data.feedback;
                    }
                }
                setSubmissions((current) => {
                    return current.map((submission) => {
                        if (submission.id !== updatedSubmission.id) return submission;
                        submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        setTimeout(() => {
                            if (isGradesGenerating) {
                                setForm(form);
                            }
                            if (isFeedbackGenerating) {
                                setForm({ ...form, feedback: updatedSubmission.ai.feedback });
                            }
                            setGradeOverrides(gradeOverrides);
                            setMeta(meta);
                        }, 50);
                        return updatedSubmission;
                    });
                });
                setIsGradesGenerating(false);
                setIsFeedbackGenerating(false);
            }
        });
        return cleanup;
    }, [
        currentSubmission,
        form,
        gradeOverrides,
        isFeedbackGenerating,
        isGradesGenerating,
        meta,
        onMessage,
        setSubmissions,
        tab,
    ]);

    useEffect(() => {
        if (selectedCriterionId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedCriterionId]);

    useEffect(() => {
        if (selectedFeedbackId) {
            setSelectedInlineCommentId(null);
        }
    }, [selectedFeedbackId]);

    useEffect(() => {
        if (selectedInlineCommentId !== null) {
            if (tab === 'grades') {
                setSelectedCriterionId(null);
            }
            if (tab === 'feedback') {
                setSelectedFeedbackId(null);
            }
        }
    }, [selectedInlineCommentId, tab]);

    if (submissions?.length && currentSubmissionId && !currentSubmission) {
        return <Redirect to={`/assignments/${currentAssignmentId}`} />;
    }

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                submissions,
                headerNavigationOptions,
                studentAccountUsers,
                currentSubmissionId,
                currentSubmission,
                selectedCriterionTitle,
                highlights: getHighlights(),
                selectedFileId,
                setSelectedFileId,
                sort,
                setSort,
                form,
                setForm,
                isCurrentFormValid,
                gradeOverrides,
                setGradeOverrides,
                meta,
                tab,
                setTab,
                selectedCriterionId,
                setSelectedCriterionId,
                selectedFeedbackId,
                setSelectedFeedbackId,
                selectedInlineCommentId,
                setSelectedInlineCommentId,
                isGradesGenerating,
                isFeedbackGenerating,
                showFailedDialog,
                setShowFailedDialog,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                handleClickAcceptSuggestions,
                handleClickConfirm,
                handleClickNext,
                handleSelectSubmission,
                handleClickClose,
                handleClickEdit,
                handleClickRegenerateFeedback,
                handleSelectDropdown,
                handleCriterionNavigation,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
